import { useState, useRef, useEffect } from 'react';
import { useCurrency } from '@/lib/currencyContext';

// Currency → ISO-2 country code for flagcdn (works reliably on Windows/Linux
// where emoji-flag glyphs aren't rendered).
const CURRENCY_CC = {
  PLN: 'pl', EUR: 'eu', USD: 'us', GBP: 'gb',
  CZK: 'cz', UAH: 'ua', RUB: 'ru', CHF: 'ch',
  SEK: 'se', NOK: 'no', DKK: 'dk', HUF: 'hu',
  RON: 'ro', BGN: 'bg',
};

function Flag({ currency, size = 20 }) {
  const cc = CURRENCY_CC[currency];
  if (!cc) return <span className="text-xs text-white/40">{currency.slice(0, 2)}</span>;
  return (
    <img
      src={`/flags/${cc}.png`}
      width={size} height={Math.round(size * 0.75)}
      alt={`${currency} flag`}
      loading="lazy" decoding="async"
      className="rounded-sm shrink-0 object-cover"
    />
  );
}

export default function CurrencySwitcher() {
  const { currency, setCurrency, supportedCurrencies, symbol } = useCurrency();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  return (
    <div ref={ref} className="relative" data-testid="currency-switcher">
      <button onClick={() => setOpen(!open)} data-testid="currency-toggle"
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors border border-white/10">
        <Flag currency={currency} size={18} />
        <span className="text-xs text-white/80 font-medium">{currency}</span>
        <span className="text-[10px] text-white/40 hidden sm:inline">{symbol}</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden shadow-2xl z-50 min-w-[140px] max-h-[60vh] overflow-y-auto">
          {supportedCurrencies.map(c => (
            <button key={c} data-testid={`currency-${c}`}
              onClick={() => { setCurrency(c); setOpen(false); }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                currency === c ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)]' : 'text-white/60 hover:bg-white/5 hover:text-white'
              }`}>
              <Flag currency={c} size={18} />
              <span className="font-medium">{c}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
